// frontend\src\components\Forms\RequirementForm.js

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Form, Button, Row, Col, Alert, InputGroup } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { handleError } from '../../utils/errorHandling';
import { sanitizeInput, sanitizeNumeric, sanitizeMultilineText } from '../../utils/sanitize';
import { formatDate } from '../../utils/dateUtils';
import { roundMoney } from '../../utils/formatters';
import DateService from '../../services/DateService';
import {
  createRequirement,
  updateRequirement,
  patchRequirement,
  refreshBankAccountsData,
  previewRequirementEffectiveChange,
  getSeriesHistory,
  previewScopedAmountChange,
  applyScopedAmountChange,
  previewScopedInactiveChange,
  applyScopedInactiveChange,
  patchMetadataFromFortnight,
  previewRequirementTypeConversion,
  applyRequirementTypeConversion,
} from '../../services/RequirementService';
import {
  formatEffectiveRange,
  fortnightEndFromStart,
  parseDateOnly,
  pickDisplaySegment,
} from '../../utils/requirementCalculations';
import { getCategories } from '../../services/CategoryService';
import { getBankAccounts, updateBankAccountAmountFromRequirements } from '../../services/BankAccountService';


// Validation functions
const validateAmount = (amount) => {
  const numAmount = parseFloat(amount);
  if (!isNaN(numAmount) &&
      numAmount > 0 &&
      numAmount < 1000000 &&
      amount.toString().length < 10) {
    return undefined;
  }
  return 'Invalid amount';
};

const validateDueDate = (date) => {
  const now = new Date();
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 1);
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  if (date >= minDate && date <= maxDate) {
    return undefined;
  }
  return 'Please enter a date between last year and 2 years from now';
};

const validateFrequency = (frequency) => {
  const validFrequencies = ['weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly'];
  if (validFrequencies.includes(frequency)) {
    return undefined;
  }
  return 'Invalid frequency';
};

const validateName = (name) => {
  if (name && name.length > 0 && name.length <= 50) {
    return undefined;
  }
  return 'Name must be between 1 and 50 characters';
};

const validateDescription = (description) => {
  if (!description || description.length <= 100) {
    return undefined;
  }
  return 'Description must be 100 characters or less';
};

const validateRequiredFields = (data, { skipNextDueDate = false } = {}) => {
  const errors = {};
  if (!data.name) errors.name = 'Name is required';
  if (!data.amount || data.amount <= 0) errors.amount = 'Valid amount required';
  if (!skipNextDueDate && !data.next_due_date) {
    errors.next_due_date = 'Next due date is required';
  }
  if (!data.frequency) errors.frequency = 'Frequency is required';
  return errors;
};

const validateLengths = (data) => {
  const errors = {};
  if (data.name && data.name.length > 50) errors.name = 'Name must be 50 characters or less';
  if (data.description && data.description.length > 100) errors.description = 'Description must be 100 characters or less';
  return errors;
};

const isRequirementSegmentActive = (req) => {
  if (!req?.effective_until_date) return true;
  const until = new Date(req.effective_until_date);
  until.setHours(23, 59, 59, 999);
  return until >= new Date();
};

const buildBackendScopedPayload = (editScope, opts) => {
  const { amount, fortnightCount, scopedAnchorDate, periodFrom, periodUntil } = opts;
  const payload = { amount: roundMoney(amount) };
  if (editScope === 'one_fortnight') {
    return {
      ...payload,
      scope: 'fortnight_count',
      fortnight_count: 1,
      ...(scopedAnchorDate ? { anchor_date: formatDate(scopedAnchorDate) } : {}),
    };
  }
  if (editScope === 'multiple_fortnights') {
    return {
      ...payload,
      scope: 'fortnight_count',
      fortnight_count: parseInt(fortnightCount, 10),
      ...(scopedAnchorDate ? { anchor_date: formatDate(scopedAnchorDate) } : {}),
    };
  }
  if (editScope === 'period') {
    return {
      ...payload,
      scope: 'period',
      period_from: formatDate(periodFrom),
      ...(periodUntil ? { period_until: formatDate(periodUntil) } : {}),
    };
  }
  return null;
};

const scopedAmountApplyNeeded = (editScope, amountChanged, segmentActive) =>
  editScope !== 'indefinite' && amountChanged && segmentActive;

const scopedInactiveApplyNeeded = (editScope, segmentActive) =>
  editScope !== 'indefinite' && !segmentActive;

const indefiniteAmountApplyNeeded = (editScope, amountChanged, segmentActive) =>
  editScope === 'indefinite' && amountChanged && segmentActive;

const SCHEDULED_CHANGES_VISIBLE = 5;

const segmentIsPast = (seg, fortnightStart) => {
  if (!seg?.effective_until_date || !fortnightStart) return false;
  const until = parseDateOnly(seg.effective_until_date);
  return until && until < fortnightStart;
};

const buildIndefiniteScopePayload = (currentFortnightStart, amount) => ({
  scope: 'period',
  period_from: formatDate(currentFortnightStart),
  amount: roundMoney(amount),
});

const DISPLAY_KIND_LABELS = {
  allocation: 'Allocation — funding only, no bills',
  bill_estimated: 'Bill (estimated) — planned bill amount',
  bill_actual: 'Bill (actual) — tracked recurring bill',
};

const BILL_DISPLAY_KINDS = new Set(['bill_estimated', 'bill_actual']);

const isBillDisplayKind = (kind) => BILL_DISPLAY_KINDS.has(kind);

const getRequirementTypeLabel = (displayKind, isAccountBase) => {
  if (isAccountBase && displayKind === 'allocation') {
    return 'Allocation (Baseline) — funding only, no bills';
  }
  return DISPLAY_KIND_LABELS[displayKind] || displayKind;
};

const BASE_ALLOCATION_HELP_TEXT =
  'Sets a minimum for this account’s required transfer total.';

const FREQUENCY_LABELS = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
};

const buildScopeOnlyPayload = (editScope, opts) => {
  const payload = buildBackendScopedPayload(editScope, {
    ...opts,
    amount: roundMoney(opts.amount || 1),
  });
  const { amount, ...scopePayload } = payload;
  return scopePayload;
};

const defaultPeriodFromDate = (currentFortnightStart) => {
  if (currentFortnightStart) return new Date(currentFortnightStart);
  return new Date();
};

const nextFortnightStartDate = (currentFortnightStart) => {
  if (!currentFortnightStart) return new Date();
  const d = new Date(currentFortnightStart);
  d.setDate(d.getDate() + 14);
  return d;
};

const resolveBillsScopeWindow = (editScope, {
  currentFortnightStart,
  scopedAnchorDate,
  fortnightCount,
  periodFrom,
  periodUntil,
  scopedPreview,
}) => {
  if (scopedPreview?.start_fortnight_date) {
    return {
      from: scopedPreview.start_fortnight_date,
      until: scopedPreview.end_fortnight_date || null,
    };
  }
  if (editScope === 'indefinite') {
    return {
      from: currentFortnightStart ? formatDate(currentFortnightStart) : formatDate(new Date()),
      until: null,
    };
  }
  if (editScope === 'one_fortnight' || editScope === 'multiple_fortnights') {
    const startDate = scopedAnchorDate || nextFortnightStartDate(currentFortnightStart);
    const endDate = new Date(startDate);
    const count = editScope === 'one_fortnight' ? 1 : (parseInt(fortnightCount, 10) || 1);
    endDate.setDate(endDate.getDate() + 13 + 14 * (count - 1));
    return {
      from: formatDate(startDate),
      until: formatDate(endDate),
    };
  }
  if (editScope === 'period') {
    const fromDate = periodFrom || defaultPeriodFromDate(currentFortnightStart);
    return {
      from: formatDate(fromDate),
      until: periodUntil ? formatDate(periodUntil) : null,
    };
  }
  return { from: null, until: null };
};

const buildBillScopePayload = (updateBillsInScope, editScope, opts) => {
  if (!updateBillsInScope) return {};
  const window = resolveBillsScopeWindow(editScope, opts);
  return {
    update_bills_in_scope: true,
    bills_scope_from: window.from,
    bills_scope_until: window.until,
  };
};

const RequirementForm = ({
  requirement,
  onSaved,
  onCancel,
  viewedFortnightStart = null,
  viewedFortnightEnd = null,
}) => {
  // Helper function to get saved values from localStorage for new requirements
  const getSavedFormValues = () => {
    if (requirement) return {}; // Don't use saved values when editing
    
    try {
      const saved = localStorage.getItem('requirementFormDefaults');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const savedValues = getSavedFormValues();

  // Helper function to save current form values to localStorage (only for new requirements)
  const saveFormDefaults = () => {
    if (requirement) return; // Don't save when editing
    
    try {
      const valuesToSave = {
        frequency,
        autoGenerate,
        minUnpaidBills,
        initialBills,
        categoryId,
        bankAccountId,
        displayKind,
      };
      localStorage.setItem('requirementFormDefaults', JSON.stringify(valuesToSave));
    } catch {
      // Silently fail if localStorage is not available
    }
  };

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState(savedValues.frequency || 'monthly');
  const [nextDueDate, setNextDueDate] = useState(new Date());
  // active removed from backend; derive effectiveness from dates instead
  const [updateBillsInScope, setUpdateBillsInScope] = useState(false);
  const [lastSubmitTime, setLastSubmitTime] = useState(Date.now());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [autoGenerate, setAutoGenerate] = useState(
    requirement?.auto_generate ?? savedValues.autoGenerate ?? true
  );
  const [minUnpaidBills, setMinUnpaidBills] = useState(savedValues.minUnpaidBills || 3);
  const [initialBills, setInitialBills] = useState(savedValues.initialBills || 3);
  const [generateNow, setGenerateNow] = useState(false); // New state for "Generate bills now"
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(savedValues.categoryId || '');
  const [notes, setNotes] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountId, setBankAccountId] = useState(savedValues.bankAccountId || '');
  const [displayKind, setDisplayKind] = useState(
    requirement?.display_kind ?? savedValues.displayKind ?? 'allocation'
  );
  const [isAccountBase, setIsAccountBase] = useState(!!requirement?.is_account_base);
  const [excludeFromRequiredAmount, setExcludeFromRequiredAmount] = useState(
    !!requirement?.exclude_from_required_amount
  );
  const [additiveToTotal, setAdditiveToTotal] = useState(!!requirement?.additive_to_total);
  
  // New state for the effective from date (fortnight start date)
  const [effectiveFromDate, setEffectiveFromDate] = useState(null);
  const [fortnightStartDates, setFortnightStartDates] = useState([]);
  const [originalAmount, setOriginalAmount] = useState(null);
  const [amountChanged, setAmountChanged] = useState(false);
  const [editScope, setEditScope] = useState('indefinite');
  const [segmentActive, setSegmentActive] = useState(true);
  const [originalSegmentActive, setOriginalSegmentActive] = useState(true);
  const [fortnightCount, setFortnightCount] = useState(4);
  const [scopedAnchorDate, setScopedAnchorDate] = useState(null);
  const [periodFrom, setPeriodFrom] = useState(null);
  const [periodUntil, setPeriodUntil] = useState(null);
  const [scopedPreview, setScopedPreview] = useState(null);
  const [scopedPreviewError, setScopedPreviewError] = useState('');
  const [scopedPreviewLoading, setScopedPreviewLoading] = useState(false);
  const [seriesHistory, setSeriesHistory] = useState([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);
  const [originalName, setOriginalName] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [originalNotes, setOriginalNotes] = useState('');
  const previewDebounceRef = useRef(null);

  // New state for the effective until date (fortnight end date)
  const [effectiveUntilDate, setEffectiveUntilDate] = useState(null);
  const [fortnightEndDates, setFortnightEndDates] = useState([]);
  const [currentFortnightStart, setCurrentFortnightStart] = useState(null);
  const [impactedBills, setImpactedBills] = useState([]);
  const [confirmBillDeletion, setConfirmBillDeletion] = useState(false);
  const [conversionTargetKind, setConversionTargetKind] = useState(requirement?.display_kind || 'allocation');
  const [conversionFrequency, setConversionFrequency] = useState(requirement?.frequency || 'monthly');
  const [conversionNextDueDate, setConversionNextDueDate] = useState(new Date());
  const [conversionAutoGenerate, setConversionAutoGenerate] = useState(requirement?.auto_generate ?? true);
  const [conversionMinUnpaidBills, setConversionMinUnpaidBills] = useState(
    requirement?.min_unpaid_bills ?? 3
  );
  const [conversionCreateInitialBills, setConversionCreateInitialBills] = useState(false);
  const [conversionInitialBillsCount, setConversionInitialBillsCount] = useState(1);
  const [conversionKeepSeries, setConversionKeepSeries] = useState(true);
  const [conversionConfirmName, setConversionConfirmName] = useState('');
  const [conversionPreview, setConversionPreview] = useState(null);
  const [conversionPreviewLoading, setConversionPreviewLoading] = useState(false);
  const [conversionError, setConversionError] = useState('');
  const [showConvertPanel, setShowConvertPanel] = useState(false);
  const [showUpcomingBillsPanel, setShowUpcomingBillsPanel] = useState(false);
  const conversionPreviewDebounceRef = useRef(null);

  const applySegmentToForm = (seg) => {
    if (!seg) return;
    setSelectedSegmentId(seg.id);
    setName(seg.name || '');
    setDescription(seg.description || '');
    setNotes(seg.notes || '');
    setAmount(seg.amount != null ? roundMoney(seg.amount) : '');
    setOriginalAmount(seg.amount != null ? roundMoney(seg.amount) : '');
    setOriginalName(seg.name || '');
    setOriginalDescription(seg.description || '');
    setOriginalNotes(seg.notes || '');
    const active = isRequirementSegmentActive(seg);
    setSegmentActive(active);
    setOriginalSegmentActive(active);
    if (seg.next_due_date) setNextDueDate(new Date(seg.next_due_date));
    if (seg.auto_generate !== undefined) setAutoGenerate(seg.auto_generate);
    if (seg.min_unpaid_bills != null) setMinUnpaidBills(seg.min_unpaid_bills);
    setConversionTargetKind(seg.display_kind || 'allocation');
    setConversionFrequency(seg.frequency || 'monthly');
    setConversionNextDueDate(seg.next_due_date ? new Date(seg.next_due_date) : new Date());
    setConversionAutoGenerate(seg.auto_generate ?? true);
    setConversionMinUnpaidBills(seg.min_unpaid_bills ?? 3);
    setConversionCreateInitialBills(false);
    setConversionInitialBillsCount(1);
    setConversionKeepSeries(true);
    setConversionConfirmName('');
    setConversionPreview(null);
    setConversionError('');
  };

  const selectedSegment = seriesHistory.find((s) => s.id === selectedSegmentId) || requirement;
  const selectedSegmentIsPast = selectedSegment && currentFortnightStart
    ? segmentIsPast(selectedSegment, currentFortnightStart)
    : false;

  const metadataChanged = requirement && (
    name !== originalName
    || description !== originalDescription
    || notes !== originalNotes
  );

  const getSegmentIdAtCurrentFortnight = () => {
    if (!requirement) return null;
    if (currentFortnightStart && seriesHistory.length) {
      const end = fortnightEndFromStart(currentFortnightStart);
      const seg = pickDisplaySegment(seriesHistory, currentFortnightStart, end);
      if (seg?.id) return seg.id;
    }
    return selectedSegmentId || requirement.id;
  };

  // Initialize form with requirement data if editing
  useEffect(() => {
    if (requirement) {
      applySegmentToForm(requirement);
      setFrequency(requirement.frequency || 'monthly');
      setCategoryId(requirement.category || '');
      setBankAccountId(requirement.bank_account || '');
      setDisplayKind(requirement.display_kind || 'allocation');
      setIsAccountBase(!!requirement.is_account_base);
      setExcludeFromRequiredAmount(!!requirement.exclude_from_required_amount);
      setAdditiveToTotal(!!requirement.additive_to_total);
      setEffectiveUntilDate(requirement.effective_until_date ? new Date(requirement.effective_until_date) : null);
      setEffectiveFromDate(requirement.effective_from_date ? new Date(requirement.effective_from_date) : null);
      setEditScope('indefinite');
      setUpdateBillsInScope(false);
      setSelectedSegmentId(requirement.id);
    }
  }, [requirement]);

  useEffect(() => {
    if (requirement && editScope === 'period' && !periodFrom && currentFortnightStart) {
      setPeriodFrom(new Date(currentFortnightStart));
    }
  }, [requirement, editScope, periodFrom, currentFortnightStart]);

  useEffect(() => {
    // Check if amount has changed compared to original
    if (originalAmount !== null) {
      // Convert both to numbers for comparison to avoid string comparison issues
      const currentAmountNum = parseFloat(amount);
      const originalAmountNum = parseFloat(originalAmount);
      
      // Compare with a small epsilon to handle floating point issues
      const hasChanged = Math.abs(currentAmountNum - originalAmountNum) > 0.001;
      setAmountChanged(hasChanged);
    } else {
      setAmountChanged(false);
    }
  }, [amount, originalAmount]);

  useEffect(() => {
    if (!requirement?.id) {
      setSeriesHistory([]);
      return undefined;
    }
    let cancelled = false;
    getSeriesHistory(requirement.id)
      .then((data) => {
        if (cancelled) return;
        const segments = data || [];
        setSeriesHistory(segments);
        if (segments.length === 0) return;
        const centerStart = viewedFortnightStart || currentFortnightStart;
        const centerEnd = viewedFortnightEnd || (centerStart ? fortnightEndFromStart(centerStart) : null);
        const displaySeg = (centerStart && centerEnd)
          ? pickDisplaySegment(segments, centerStart, centerEnd)
          : segments.find((s) => s.id === requirement.id) || segments[0];
        if (displaySeg) applySegmentToForm(displaySeg);
      })
      .catch(() => {
        if (!cancelled) setSeriesHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [requirement?.id, viewedFortnightStart, viewedFortnightEnd, currentFortnightStart]);

  useEffect(() => {
    if (!requirement || editScope === 'indefinite') {
      setScopedPreview(null);
      setScopedPreviewError('');
      return undefined;
    }

    const scopeOpts = {
      amount,
      fortnightCount,
      scopedAnchorDate,
      periodFrom: periodFrom || defaultPeriodFromDate(currentFortnightStart),
      periodUntil,
    };

    if (scopedInactiveApplyNeeded(editScope, segmentActive)) {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
      previewDebounceRef.current = setTimeout(async () => {
        setScopedPreviewLoading(true);
        setScopedPreviewError('');
        try {
          const preview = await previewScopedInactiveChange(
            requirement.id,
            {
              ...buildScopeOnlyPayload(editScope, scopeOpts),
              resume_amount: roundMoney(amount),
            }
          );
          setScopedPreview(preview);
        } catch (error) {
          setScopedPreview(null);
          const detail = error.response?.data;
          const message = typeof detail === 'string'
            ? detail
            : detail?.scope || detail?.error || detail?.period_until || 'Could not preview inactive period';
          setScopedPreviewError(Array.isArray(message) ? message[0] : message);
        } finally {
          setScopedPreviewLoading(false);
        }
      }, 400);

      return () => {
        if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
      };
    }

    if (!scopedAmountApplyNeeded(editScope, amountChanged, segmentActive) || !amount) {
      setScopedPreview(null);
      setScopedPreviewError('');
      return undefined;
    }
    if (validateAmount(amount)) {
      setScopedPreview(null);
      return undefined;
    }
    if (editScope === 'multiple_fortnights' && (!fortnightCount || parseInt(fortnightCount, 10) < 1)) {
      setScopedPreview(null);
      return undefined;
    }
    if (editScope === 'period' && !periodFrom && !currentFortnightStart) {
      setScopedPreview(null);
      return undefined;
    }

    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      setScopedPreviewLoading(true);
      setScopedPreviewError('');
      try {
        const payload = buildBackendScopedPayload(editScope, scopeOpts);
        const preview = await previewScopedAmountChange(requirement.id, payload);
        setScopedPreview(preview);
      } catch (error) {
        setScopedPreview(null);
        const detail = error.response?.data;
        const message = typeof detail === 'string'
          ? detail
          : detail?.scope || detail?.error || detail?.period_from || 'Could not preview change';
        setScopedPreviewError(Array.isArray(message) ? message[0] : message);
      } finally {
        setScopedPreviewLoading(false);
      }
    }, 400);

    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, [
    requirement,
    editScope,
    segmentActive,
    amountChanged,
    amount,
    fortnightCount,
    scopedAnchorDate,
    periodFrom,
    periodUntil,
    currentFortnightStart,
  ]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [categoriesData, bankAccountsData] = await Promise.all([
          getCategories(),
          getBankAccounts()
        ]);
        setCategories(categoriesData);
        setBankAccounts(bankAccountsData);
        
        // Restore saved values for new requirements (after data is loaded)
        if (!requirement && savedValues.bankAccountId && bankAccountsData.some(ba => ba.id === parseInt(savedValues.bankAccountId))) {
          setBankAccountId(savedValues.bankAccountId);
        }
        if (!requirement && savedValues.categoryId && categoriesData.some(cat => cat.id === parseInt(savedValues.categoryId))) {
          setCategoryId(savedValues.categoryId);
        }
        
        // Generate fortnight start dates (8 fortnights forward from today)
        generateFortnightDates();
        // Generate fortnight end dates (8 fortnights forward from today)
        generateFortnightEndDates();
      } catch (error) {
        console.error("Error loading data:", error.name);
        setErrorMessage("Failed to load data");
      }
    };

    loadData();
  }, []);
  
  // Function to generate fortnight start dates based on tenant base date
  const generateFortnightDates = async () => {
    const dates = [];
    try {
      const resp = await DateService.getFortnightStartDate();
      const iso = resp?.fortnights?.[0]?.start_date;
      if (!iso) throw new Error('No base fortnight start returned');
      // Parse YYYY-MM-DD safely in local time to avoid TZ shifts
      const [y, m, d] = iso.split('-').map(Number);
      const base = new Date(y, m - 1, d);
      setCurrentFortnightStart(base);
      for (let i = 0; i < 8; i++) {
        const dt = new Date(base);
        dt.setDate(base.getDate() + i * 14);
        dates.push({
          date: dt,
          label: dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
        });
      }
      setFortnightStartDates(dates);
      // Prefer requirement's stored effective_from_date if present
      let desired = requirement && requirement.effective_from_date
        ? new Date(requirement.effective_from_date)
        : effectiveFromDate;
      if (desired) {
        // If desired isn't exactly a start, snap to the start that covers desired
        const exact = dates.find(d => formatDate(d.date) === formatDate(desired));
        if (exact) {
          setEffectiveFromDate(exact.date);
        } else {
          const covering = dates.find(d => {
            const start = d.date;
            const end = new Date(start);
            end.setDate(start.getDate() + 13);
            return desired >= start && desired <= end;
          });
          if (covering) setEffectiveFromDate(covering.date);
          else if (!effectiveFromDate && dates.length > 0) setEffectiveFromDate(dates[0].date);
        }
      } else if (dates.length > 0) {
        // No desired date yet; default to the current start
        setEffectiveFromDate(dates[0].date);
      }
    } catch (e) {
      setFortnightStartDates([]);
    }
  };

  // Function to generate fortnight end dates from tenant base date
  const generateFortnightEndDates = async () => {
    const dates = [];
    try {
      const resp = await DateService.getFortnightStartDate();
      const iso = resp?.fortnights?.[0]?.start_date;
      if (!iso) throw new Error('No base fortnight start returned');
      const [y, m, d] = iso.split('-').map(Number);
      const base = new Date(y, m - 1, d);
      for (let i = 0; i < 8; i++) {
        const start = new Date(base);
        start.setDate(base.getDate() + i * 14);
        const end = new Date(start);
        end.setDate(start.getDate() + 13);
        dates.push({
          date: end,
          label: end.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
        });
      }
      setFortnightEndDates(dates);
    } catch (e) {
      setFortnightEndDates([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    switch(name) {
      case 'name':
        setName(sanitizeMultilineText(value));
        break;
      case 'description':
        setDescription(sanitizeMultilineText(value));
        break;
      case 'amount':
        setAmount(sanitizeNumeric(value));
        break;
      case 'frequency':
        setFrequency(value);
        break;
      // 'active' removed
      case 'updateBillsInScope':
        setUpdateBillsInScope(checked);
        break;
      case 'notes':
        setNotes(sanitizeMultilineText(value));
        break;
      case 'bank_account':
        setBankAccountId(value);
        break;
      default:
        break;
    }
  };

  const conversionChanged = requirement && conversionTargetKind !== displayKind;
  const convertingToBill = isBillDisplayKind(conversionTargetKind);
  const convertingToAllocation = conversionTargetKind === 'allocation' && displayKind !== 'allocation';
  const isBillLabelConversion =
    conversionChanged && isBillDisplayKind(displayKind) && isBillDisplayKind(conversionTargetKind);
  const needsApiConversionPreview = conversionChanged && !isBillLabelConversion;

  const validateForm = (data) => {
    const errors = {};

    const amountError = validateAmount(data.amount);
    if (amountError) errors.amount = amountError;

    const skipNextDueDate = data.display_kind === 'allocation';
    if (!skipNextDueDate) {
      const dateError = validateDueDate(data.next_due_date);
      if (dateError) errors.next_due_date = dateError;
    }

    const frequencyError = validateFrequency(data.frequency);
    if (frequencyError) errors.frequency = frequencyError;

    const nameError = validateName(data.name);
    if (nameError) errors.name = nameError;

    const descriptionError = validateDescription(data.description);
    if (descriptionError) errors.description = descriptionError;

    // Only merge other validations if they have errors
    const requiredErrors = validateRequiredFields(data, {
      skipNextDueDate: data.display_kind === 'allocation',
    });
    if (Object.keys(requiredErrors).length > 0) Object.assign(errors, requiredErrors);

    const lengthErrors = validateLengths(data);
    if (Object.keys(lengthErrors).length > 0) Object.assign(errors, lengthErrors);

    return errors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Prevent double-submit
    if (isSubmitting) return;

    // Rate limiting check
    if (Date.now() - lastSubmitTime < 1000) {
      setErrorMessage('Please wait before submitting again');
      return;
    }
    setLastSubmitTime(Date.now());
    setIsSubmitting(true);

    // Form validation
    const allocationNextDue =
      displayKind === 'allocation'
        ? currentFortnightStart || new Date()
        : nextDueDate;

    const formData = {
      name,
      description,
      amount,
      frequency,
      next_due_date: allocationNextDue,
      auto_generate: autoGenerate,
      min_unpaid_bills: minUnpaidBills,
      initial_bills: initialBills,
      // active removed
      notes: notes,
      category: categoryId || null,
        bank_account: bankAccountId || null,
        display_kind: displayKind,
        is_account_base: isAccountBase,
        exclude_from_required_amount: excludeFromRequiredAmount,
        additive_to_total: additiveToTotal,
        effective_until_date: effectiveUntilDate
    };

    const errors = validateForm(formData);
    
    if (requirement && selectedSegmentIsPast) {
      errors.segment = 'This scheduled change has already ended and cannot be edited';
    }

    if (requirement && scopedInactiveApplyNeeded(editScope, segmentActive)) {
      if (editScope === 'period' && !periodFrom && !currentFortnightStart) {
        errors.periodFrom = 'Start date is required for a period change';
      }
      if (!scopedPreview && !scopedPreviewLoading) {
        errors.scopedPreview = scopedPreviewError || 'Could not preview inactive period';
      }
    } else if (requirement && indefiniteAmountApplyNeeded(editScope, amountChanged, segmentActive)) {
      if (!currentFortnightStart) {
        errors.scopedPreview = 'Could not determine the current fortnight';
      }
    } else if (requirement && scopedAmountApplyNeeded(editScope, amountChanged, segmentActive)) {
      if (editScope === 'multiple_fortnights') {
        const count = parseInt(fortnightCount, 10);
        if (!count || count < 1 || count > 104) {
          errors.fortnightCount = 'Enter a number of fortnights between 1 and 104';
        }
      }
      if (editScope === 'period' && !periodFrom && !currentFortnightStart) {
        errors.periodFrom = 'Start date is required for a period change';
      }
      if (!scopedPreview && !scopedPreviewLoading) {
        errors.scopedPreview = scopedPreviewError || 'Could not preview this change';
      }
    }

    if (conversionChanged) {
      if (needsApiConversionPreview && !conversionPreview && !conversionPreviewLoading) {
        errors.conversion = conversionError || 'Conversion impact is still loading. Wait a moment and try again.';
      }
      if (convertingToBill) {
        if (!conversionNextDueDate) {
          errors.conversionNextDueDate = 'Next due date is required.';
        }
        if (conversionAutoGenerate && (conversionMinUnpaidBills < 1 || conversionMinUnpaidBills > 12)) {
          errors.conversionMinUnpaid = 'Minimum unpaid bills must be between 1 and 12.';
        }
        if (conversionCreateInitialBills && (conversionInitialBillsCount < 1 || conversionInitialBillsCount > 6)) {
          errors.conversionInitialBills = 'Initial bill count must be between 1 and 6.';
        }
      }
      if (convertingToAllocation) {
        if (conversionConfirmName.trim() !== (name || '').trim()) {
          errors.conversionConfirmName = 'Type the exact requirement name to confirm conversion.';
        }
      }
    }
    
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setErrorMessage('Please check your input');
      setIsSubmitting(false);
      return;
    }

    try {
      const deriveEffectiveUntil = () => {
        if (editScope !== 'indefinite') return effectiveUntilDate ? formatDate(effectiveUntilDate) : null;
        if (segmentActive) return null;
        if (currentFortnightStart) return formatDate(fortnightEndFromStart(currentFortnightStart));
        return requirement?.effective_until_date || null;
      };
      const proposedUntil = deriveEffectiveUntil();

      if (requirement && editScope === 'indefinite' && !segmentActive) {
        const inactiveSegId = getSegmentIdAtCurrentFortnight();
        const inactiveSeg = seriesHistory.find((s) => s.id === inactiveSegId) || requirement;
        const oldUntil = inactiveSeg.effective_until_date || null;
        const newUntil = proposedUntil;
        const changedUntil = oldUntil !== newUntil;
        if (changedUntil) {
          const preview = await previewRequirementEffectiveChange(inactiveSegId, {
            effective_from_date: inactiveSeg.effective_from_date || null,
            effective_until_date: proposedUntil,
          });
          setImpactedBills(preview.impacted_bills || []);
          if ((preview.impacted_bills || []).length > 0 && !confirmBillDeletion) {
            setErrorMessage('This change will delete the following unpaid bills for this requirement. Confirm below to proceed.');
            setConfirmBillDeletion(true);
            setIsSubmitting(false);
            return;
          }
        }
      }

      // Create the requirement data object with sanitized inputs
      const requirementData = {
        name: sanitizeMultilineText(name),
        description: sanitizeMultilineText(description),
        amount: roundMoney(amount),
        frequency: frequency,
        next_due_date: formatDate(nextDueDate),
        auto_generate: autoGenerate,
        min_unpaid_bills: parseInt(minUnpaidBills, 10),
        initial_bills: autoGenerate || generateNow ? parseInt(initialBills, 10) : 0,
        notes: sanitizeMultilineText(notes),
        category: categoryId && categoryId !== "" ? parseInt(categoryId, 10) : null,
        bank_account: bankAccountId && bankAccountId !== "" ? parseInt(bankAccountId, 10) : null,
        display_kind: displayKind,
        is_account_base: isAccountBase,
        exclude_from_required_amount: excludeFromRequiredAmount,
        additive_to_total: additiveToTotal,
        effective_until_date: proposedUntil,
        confirm_delete_bills: confirmBillDeletion && impactedBills.length > 0,
      };

      const selectedDate = effectiveFromDate ? formatDate(effectiveFromDate) : null;

      let updatedRequirement;

      const billScopeOpts = {
        currentFortnightStart,
        scopedAnchorDate,
        fortnightCount,
        periodFrom: periodFrom || defaultPeriodFromDate(currentFortnightStart),
        periodUntil,
        scopedPreview,
      };

      const metadataPatch = {
        name: requirementData.name,
        description: requirementData.description,
        notes: requirementData.notes,
        next_due_date: requirementData.next_due_date,
        auto_generate: !!requirementData.auto_generate,
        min_unpaid_bills: requirementData.min_unpaid_bills || 0,
      };

      const segmentActiveChanged = segmentActive !== originalSegmentActive;

      if (requirement) {
        try {
          const scopeOpts = {
            amount,
            fortnightCount,
            scopedAnchorDate,
            periodFrom: periodFrom || defaultPeriodFromDate(currentFortnightStart),
            periodUntil,
          };

          if (scopedInactiveApplyNeeded(editScope, segmentActive)) {
            const inactivePayload = {
              ...buildScopeOnlyPayload(editScope, scopeOpts),
              resume_amount: roundMoney(amount),
              ...buildBillScopePayload(updateBillsInScope, editScope, billScopeOpts),
            };
            await applyScopedInactiveChange(requirement.id, inactivePayload);
            updatedRequirement = await patchRequirement(selectedSegmentId, metadataPatch);
          } else if (scopedAmountApplyNeeded(editScope, amountChanged, segmentActive)) {
            const scopedPayload = {
              ...buildBackendScopedPayload(editScope, scopeOpts),
              ...buildBillScopePayload(updateBillsInScope, editScope, billScopeOpts),
            };
            const result = await applyScopedAmountChange(requirement.id, scopedPayload);
            const metaId = result.metadata_segment_id || selectedSegmentId;
            updatedRequirement = await patchRequirement(metaId, metadataPatch);
          } else if (indefiniteAmountApplyNeeded(editScope, amountChanged, segmentActive)) {
            const result = await applyScopedAmountChange(requirement.id, {
              ...buildIndefiniteScopePayload(currentFortnightStart, amount),
              ...buildBillScopePayload(updateBillsInScope, 'period', {
                ...billScopeOpts,
                periodFrom: currentFortnightStart,
                periodUntil: null,
              }),
            });
            if (metadataChanged) {
              await patchMetadataFromFortnight(
                requirement.id,
                formatDate(currentFortnightStart),
                metadataPatch,
              );
              updatedRequirement = await patchRequirement(
                result.metadata_segment_id || getSegmentIdAtCurrentFortnight(),
                metadataPatch,
              );
            } else {
              const metaId = result.metadata_segment_id || getSegmentIdAtCurrentFortnight();
              updatedRequirement = await patchRequirement(metaId, metadataPatch);
            }
          } else if (editScope === 'indefinite' && segmentActiveChanged) {
            const inactiveSegId = getSegmentIdAtCurrentFortnight();
            updatedRequirement = await updateRequirement(inactiveSegId, {
              ...requirementData,
              amount: requirementData.amount === '' ? '0.00' : requirementData.amount,
              min_unpaid_bills: requirementData.min_unpaid_bills || 0,
              initial_bills: 0,
              auto_generate: !!requirementData.auto_generate,
              frequency: requirement.frequency,
              category: requirement.category,
              bank_account: requirement.bank_account,
              display_kind: requirement.display_kind,
              is_account_base: requirement.is_account_base,
              exclude_from_required_amount: requirement.exclude_from_required_amount,
              additive_to_total: requirement.additive_to_total,
              effective_until_date: proposedUntil,
              ...buildBillScopePayload(updateBillsInScope, editScope, billScopeOpts),
            });
          } else if (editScope === 'indefinite' && metadataChanged) {
            await patchMetadataFromFortnight(
              requirement.id,
              formatDate(currentFortnightStart),
              metadataPatch,
            );
            updatedRequirement = await patchRequirement(selectedSegmentId, metadataPatch);
          } else {
            updatedRequirement = await patchRequirement(selectedSegmentId, metadataPatch);
          }
        } catch (error) {
          console.error("Detailed API error:", {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            requestData: requirementData
          });
          throw error;
        }
      } else {
        if (effectiveFromDate) {
          requirementData.effective_from_date = formatDate(effectiveFromDate);
        } else if (currentFortnightStart) {
          requirementData.effective_from_date = formatDate(currentFortnightStart);
        }
        updatedRequirement = await createRequirement(requirementData);
      }

      if (requirement && conversionChanged) {
        const convertReqId = updatedRequirement?.id || selectedSegmentId || requirement.id;
        const conversionPayload = {
          target_display_kind: conversionTargetKind,
          expected_segment_id: selectedSegmentId || requirement.id,
          frequency: conversionFrequency,
          keep_bill_series: conversionKeepSeries,
          auto_generate: conversionAutoGenerate,
          min_unpaid_bills: conversionMinUnpaidBills,
          next_due_date: conversionNextDueDate ? formatDate(conversionNextDueDate) : null,
          create_initial_bills: conversionCreateInitialBills,
          initial_bills_count: conversionCreateInitialBills ? conversionInitialBillsCount : 0,
          delete_future_unpaid: convertingToAllocation,
          confirm_name: conversionConfirmName,
        };
        await applyRequirementTypeConversion(convertReqId, conversionPayload);
        // Refresh selected segment metadata in UI state
        setDisplayKind(conversionTargetKind);
      }
      
      // Always update the bank account's required amount if we have a bank account
      // This is important for both new requirements and updates
      if (bankAccountId) {
        // Determine the effective date to use
        let effectiveDate = selectedDate;
        if (!effectiveDate) {
          // If no specific date was selected (for new requirements or non-amount changes),
          // use the next due date or today
          effectiveDate = formatDate(nextDueDate || new Date());
        }
        
        try {          
          // Update the bank account's required amount based on all its requirements
          await updateBankAccountAmountFromRequirements(
            parseInt(bankAccountId, 10),
            effectiveDate,
            `Updated from requirement: ${name}`
          );
          
          // Also refresh the bank accounts data display
          await refreshBankAccountsData();
        } catch (err) {
          console.error("Error updating bank account required amount:", err);
          
          // Get a more user-friendly error message
          let errorMessage = "Failed to update bank account required amount";
          
          if (err.response && err.response.data && err.response.data.error) {
            errorMessage = `Bank account update error: ${err.response.data.error}`;
          }
          
          // Set the error message to display to the user
          setErrorMessage(errorMessage);
          
          // Don't consider this a fatal error - we'll still save the requirement
          // but show the error message to the user
        }
      }
      
      // Save form defaults for next time (only for new requirements)
      if (!requirement) {
        saveFormDefaults();
      }
      
      onSaved(updatedRequirement);
      // reset confirmation state
      setImpactedBills([]);
      setConfirmBillDeletion(false);
    } catch (error) {
      handleError(error, setErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setAmount('');
    setFrequency('monthly');
    setNextDueDate(new Date());
    // no active flag anymore
    setAutoGenerate(true);
    setMinUnpaidBills(3);
    setInitialBills(3);
    setUpdateBillsInScope(false);
    setGenerateNow(false);
    setErrorMessage('');
    setValidationErrors({});
    setCategoryId('');
    setNotes('');
    setBankAccountId('');
    setEffectiveUntilDate(null);
  };

  // Add a helper function to stop event propagation
  const stopPropagation = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
  };

  const handleDisplayKindChange = (value) => {
    setDisplayKind(value);
    if (value === 'allocation') {
      setAutoGenerate(false);
      setExcludeFromRequiredAmount(false);
    }
  };

  const handleAccountBaseChange = (checked) => {
    setIsAccountBase(checked);
    if (checked) {
      setDisplayKind('allocation');
      setAutoGenerate(false);
      setAdditiveToTotal(false);
      setExcludeFromRequiredAmount(false);
    }
  };

  useEffect(() => {
    if (excludeFromRequiredAmount) {
      setAdditiveToTotal(false);
    }
  }, [excludeFromRequiredAmount]);

  useEffect(() => {
    if (additiveToTotal) {
      setExcludeFromRequiredAmount(false);
    }
  }, [additiveToTotal]);

  useEffect(() => {
    if (!autoGenerate) {
      setInitialBills(0);
      // Also reset generateNow since it's no longer relevant when auto-generate is off
      setGenerateNow(false);
    } else {
      // When autoGenerate is turned on, set initialBills to match minUnpaidBills
      setInitialBills(minUnpaidBills);
    }
  }, [autoGenerate, minUnpaidBills]);
  
  // Add a new effect to update initialBills when minUnpaidBills changes
  useEffect(() => {
    // Only sync initialBills with minUnpaidBills when autoGenerate is true
    if (autoGenerate) {
      setInitialBills(minUnpaidBills);
    }
  }, [minUnpaidBills]);

  useEffect(() => {
    if (validationErrors.conversion || validationErrors.conversionConfirmName) {
      setShowConvertPanel(true);
    }
  }, [validationErrors.conversion, validationErrors.conversionConfirmName]);

  const fetchConversionPreview = useCallback(async () => {
    if (!requirement || !needsApiConversionPreview) {
      return;
    }
    setConversionPreviewLoading(true);
    setConversionError('');
    try {
      const preview = await previewRequirementTypeConversion(requirement.id, {
        target_display_kind: conversionTargetKind,
      });
      setConversionPreview(preview);
    } catch (error) {
      const detail = error.response?.data;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.target_display_kind || detail?.error || 'Could not preview conversion';
      setConversionError(Array.isArray(msg) ? msg[0] : msg);
      setConversionPreview(null);
    } finally {
      setConversionPreviewLoading(false);
    }
  }, [requirement, needsApiConversionPreview, conversionTargetKind]);

  useEffect(() => {
    if (!requirement || !conversionChanged) {
      setConversionPreview(null);
      setConversionError('');
      setConversionPreviewLoading(false);
      return undefined;
    }

    if (isBillLabelConversion) {
      setConversionPreview({
        source_display_kind: displayKind,
        target_display_kind: conversionTargetKind,
        warnings: [
          'Label change only — existing bills and due dates stay as they are.',
        ],
        local_only: true,
      });
      setConversionError('');
      setConversionPreviewLoading(false);
      return undefined;
    }

    if (conversionPreviewDebounceRef.current) {
      clearTimeout(conversionPreviewDebounceRef.current);
    }
    conversionPreviewDebounceRef.current = setTimeout(() => {
      fetchConversionPreview();
    }, 300);

    return () => {
      if (conversionPreviewDebounceRef.current) {
        clearTimeout(conversionPreviewDebounceRef.current);
      }
    };
  }, [
    requirement,
    conversionChanged,
    isBillLabelConversion,
    displayKind,
    conversionTargetKind,
    fetchConversionPreview,
  ]);

  const conversionPreviewSummary = useMemo(() => {
    if (!conversionChanged) return null;
    if (conversionPreviewLoading) {
      return 'Checking conversion impact…';
    }
    if (conversionError) {
      return conversionError;
    }
    if (!conversionPreview) {
      return 'Preparing conversion preview…';
    }
    if (conversionPreview.local_only) {
      return conversionPreview.warnings?.[0] || 'Ready to change label.';
    }
    const parts = [];
    if (conversionPreview.warnings?.length) {
      parts.push(conversionPreview.warnings[0]);
    }
    const deleteCount = conversionPreview.delete_summary?.future_unpaid_count;
    if (deleteCount > 0) {
      parts.push(
        `Will delete ${deleteCount} future unpaid bill(s)${
          conversionPreview.delete_summary.first_due_date
            ? ` (${conversionPreview.delete_summary.first_due_date} to ${conversionPreview.delete_summary.last_due_date})`
            : ''
        }.`
      );
    }
    if (convertingToAllocation && conversionConfirmName.trim() !== (name || '').trim()) {
      parts.push('Type the exact requirement name below before saving.');
    }
    return parts.join(' ') || 'Conversion preview ready.';
  }, [
    conversionChanged,
    conversionPreviewLoading,
    conversionError,
    conversionPreview,
    convertingToAllocation,
    conversionConfirmName,
    name,
  ]);

  return (
    <Form onSubmit={handleFormSubmit} onClick={stopPropagation}>
      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
      {Object.keys(validationErrors).map(key => (
        <Alert key={key} variant="danger">
          {key}: {validationErrors[key]}
        </Alert>
      ))}

      {requirement ? (
        <>
          {isAccountBase && (
            <Alert variant="primary" className="requirements-base-allocation-banner py-2 mb-3">
              <strong>Base allocation (min)</strong>
              <span className="d-block small mt-1 mb-0">{BASE_ALLOCATION_HELP_TEXT}</span>
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label><strong>Scope</strong></Form.Label>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button
                type="button"
                variant={editScope === 'indefinite' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setEditScope('indefinite')}
              >
                Indefinite
              </Button>
              <Button
                type="button"
                variant={editScope === 'one_fortnight' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setEditScope('one_fortnight')}
              >
                One fortnight
              </Button>
              <Button
                type="button"
                variant={editScope === 'multiple_fortnights' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setEditScope('multiple_fortnights')}
              >
                Multiple fortnights
              </Button>
              <Button
                type="button"
                variant={editScope === 'period' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setEditScope('period')}
              >
                Period
              </Button>
            </div>

            {editScope === 'indefinite' && currentFortnightStart && (
              <Form.Text className="text-muted d-block">
                Will update this requirement for all future fortnights from this fortnight (
                {currentFortnightStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                {' → '}
                {fortnightEndFromStart(currentFortnightStart).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                {' and ongoing'}).
              </Form.Text>
            )}

            {(editScope === 'one_fortnight' || editScope === 'multiple_fortnights') && (
              <>
                {editScope === 'multiple_fortnights' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Number of fortnights</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="104"
                      value={fortnightCount}
                      onChange={(e) => setFortnightCount(e.target.value)}
                      isInvalid={!!validationErrors.fortnightCount}
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.fortnightCount}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Starting fortnight</Form.Label>
                  <Form.Select
                    value={scopedAnchorDate ? formatDate(scopedAnchorDate) : ''}
                    onChange={(e) => {
                      if (!e.target.value) {
                        setScopedAnchorDate(null);
                        return;
                      }
                      const selectedDate = fortnightStartDates.find(
                        (d) => formatDate(d.date) === e.target.value
                      )?.date;
                      setScopedAnchorDate(selectedDate || null);
                    }}
                  >
                    <option value="">Next fortnight (default)</option>
                    {fortnightStartDates.map((date, index) => (
                      <option key={index} value={formatDate(date.date)}>
                        {date.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </>
            )}

            {editScope === 'period' && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>From date</Form.Label>
                    <DatePicker
                      selected={periodFrom}
                      onChange={(date) => setPeriodFrom(date)}
                      dateFormat="yyyy-MM-dd"
                      minDate={currentFortnightStart || undefined}
                      className={`form-control${validationErrors.periodFrom ? ' is-invalid' : ''}`}
                      placeholderText="Start date"
                    />
                    <Form.Control.Feedback type="invalid" style={{ display: validationErrors.periodFrom ? 'block' : 'none' }}>
                      {validationErrors.periodFrom}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Until date (optional)</Form.Label>
                    <DatePicker
                      selected={periodUntil}
                      onChange={(date) => setPeriodUntil(date)}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Ongoing if blank"
                      isClearable
                    />
                    <Form.Text className="text-muted">
                      Leave blank for an open-ended change from the start date.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {seriesHistory.length > 0 && (() => {
              const selectedIdx = seriesHistory.findIndex((s) => s.id === selectedSegmentId);
              const centerIdx = selectedIdx >= 0 ? selectedIdx : 0;
              const half = Math.floor(SCHEDULED_CHANGES_VISIBLE / 2);
              let startIdx = Math.max(0, centerIdx - half);
              let endIdx = Math.min(seriesHistory.length, startIdx + SCHEDULED_CHANGES_VISIBLE);
              if (endIdx - startIdx < SCHEDULED_CHANGES_VISIBLE) {
                startIdx = Math.max(0, endIdx - SCHEDULED_CHANGES_VISIBLE);
              }
              const visible = seriesHistory.slice(startIdx, endIdx);
              const canScrollUp = startIdx > 0;
              const canScrollDown = endIdx < seriesHistory.length;

              return (
                <div className="mb-3 scheduled-changes-panel">
                  <div className="small fw-semibold mb-2">Scheduled Changes</div>
                  {canScrollUp && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0 mb-1 scheduled-changes-scroll"
                      onClick={() => applySegmentToForm(seriesHistory[startIdx - 1])}
                    >
                      ↑ Earlier
                    </Button>
                  )}
                  <ul className="list-unstyled mb-0 scheduled-changes-list">
                    {visible.map((seg) => {
                      const isSelected = seg.id === selectedSegmentId;
                      const isPast = segmentIsPast(seg, currentFortnightStart);
                      return (
                        <li key={seg.id} className="mb-1">
                          <Button
                            type="button"
                            variant={isSelected ? 'primary' : 'outline-secondary'}
                            size="sm"
                            className="w-100 text-start scheduled-changes-item"
                            onClick={() => applySegmentToForm(seg)}
                          >
                            <strong>${roundMoney(seg.amount)}</strong>
                            {' · '}
                            {formatEffectiveRange(seg)}
                            {isPast && <span className="text-muted"> (ended)</span>}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                  {canScrollDown && (
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="p-0 mt-1 scheduled-changes-scroll"
                      onClick={() => applySegmentToForm(seriesHistory[endIdx])}
                    >
                      ↓ Later
                    </Button>
                  )}
                  {selectedSegmentIsPast && (
                    <Form.Text className="text-warning d-block mt-2">
                      This scheduled change has already ended and cannot be edited.
                    </Form.Text>
                  )}
                </div>
              );
            })()}

            {editScope !== 'indefinite' && ((!segmentActive) || (segmentActive && amountChanged)) && (
              <div className="mt-2">
                {scopedPreviewLoading && (
                  <div className="text-muted small">
                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                    Previewing change…
                  </div>
                )}
                {!scopedPreviewLoading && scopedPreview && (
                  <Alert variant="info" className="mb-0 py-2">
                    <div><strong>{scopedPreview.summary}</strong></div>
                    <div className="small mt-1">
                      Starts: {scopedPreview.start_fortnight_date}
                      {scopedPreview.end_fortnight_date && ` · Ends: ${scopedPreview.end_fortnight_date}`}
                      {scopedPreview.fortnight_count != null && ` · ${scopedPreview.fortnight_count} fortnight${scopedPreview.fortnight_count === 1 ? '' : 's'}`}
                      {scopedPreview.ongoing && ' · Ongoing'}
                    </div>
                  </Alert>
                )}
                {!scopedPreviewLoading && !scopedPreview && scopedPreviewError && (
                  <Alert variant="warning" className="mb-0 py-2">
                    <small>{scopedPreviewError}</small>
                  </Alert>
                )}
                {validationErrors.scopedPreview && (
                  <div className="text-danger small mt-1">{validationErrors.scopedPreview}</div>
                )}
              </div>
            )}
          </Form.Group>

          <div className="mb-4 p-3 rounded border bg-light">
            <h6 className="mb-3">For this scope</h6>

            <Form.Group className="mb-3" controlId="requirementAmount">
              <Form.Label><strong>Amount</strong></Form.Label>
              <InputGroup onClick={stopPropagation}>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  name="amount"
                  value={amount}
                  onChange={handleInputChange}
                  placeholder="Enter amount"
                  step="0.01"
                  onClick={stopPropagation}
                  onWheel={(e) => e.target.blur()}
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="segment-active"
                label="Active"
                checked={segmentActive}
                onChange={(e) => setSegmentActive(e.target.checked)}
              />
              {editScope === 'indefinite' && (
                <Form.Text className="text-muted d-block">
                  When inactive, this requirement stops after the current fortnight.
                </Form.Text>
              )}
              {editScope !== 'indefinite' && !segmentActive && (
                <Form.Text className="text-muted d-block">
                  {editScope === 'period' && !periodUntil
                    ? 'Requirement will not apply from the start date with no end date.'
                    : 'Requirement will not apply for the selected period, then resume afterwards.'}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="requirementNotes">
              <Form.Label><strong>Notes</strong></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={notes}
                onChange={handleInputChange}
                placeholder="Optional notes for this scope"
                maxLength={100}
                style={{ resize: 'vertical' }}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="requirementName">
              <Form.Label>Requirement name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={name}
                onChange={handleInputChange}
                placeholder="e.g., Monthly Rent"
                maxLength={50}
                onClick={stopPropagation}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="requirementDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={description}
                onChange={handleInputChange}
                placeholder="Optional description"
                maxLength={100}
                style={{ resize: 'vertical' }}
                onClick={stopPropagation}
              />
            </Form.Group>

            {requirement.bill_series_id && displayKind !== 'allocation' && (
              <Form.Group className="mb-0" controlId="updateBillsInScope">
                <Form.Check
                  type="checkbox"
                  label="Update bills in scope"
                  name="updateBillsInScope"
                  checked={updateBillsInScope}
                  onChange={handleInputChange}
                />
              </Form.Group>
            )}
          </div>

          <div
            className={`mb-4${isAccountBase ? ' requirements-fixed-details-base p-3 rounded' : ''}`}
          >
            <h6 className="mb-3">Fixed Details</h6>
            <div className="mb-1">
              Requirement Type:{' '}
              <strong>{getRequirementTypeLabel(displayKind, isAccountBase)}</strong>
            </div>
            {(displayKind === 'bill_estimated' || displayKind === 'bill_actual') && excludeFromRequiredAmount && (
              <div className="mb-1">
                Funding:{' '}
                <strong>Not added to bank account required amount</strong>
              </div>
            )}
            <div className="mb-1">
              Frequency:{' '}
              <strong>{FREQUENCY_LABELS[frequency] || frequency}</strong>
            </div>
            <div className="mb-1">
              Category:{' '}
              <strong>{categories.find((c) => String(c.id) === String(categoryId))?.name || 'No category'}</strong>
            </div>
            <div>
              Bank Account:{' '}
              <strong>
                {(() => {
                  const account = bankAccounts.find((a) => String(a.id) === String(bankAccountId));
                  if (!account) return 'No bank account';
                  return `${account.nickname || 'Unnamed'} — ${account.bank || 'Unknown Bank'}`;
                })()}
              </strong>
            </div>
          </div>
        </>
      ) : (
        <>
          <Form.Group className="mb-3" controlId="requirementName">
            <Form.Label>Requirement Name:</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={name}
              onChange={handleInputChange}
              placeholder="e.g., Monthly Rent"
              maxLength={50}
              onClick={stopPropagation}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="requirementDescription">
            <Form.Label>Description:</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={description}
              onChange={handleInputChange}
              placeholder="Optional description"
              maxLength={100}
              style={{ resize: 'vertical' }}
              onClick={stopPropagation}
            />
            <Form.Text className="text-muted">
              You can use spaces and line breaks (Enter) in this field
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="requirementDisplayKind">
            <Form.Label>Requirement type</Form.Label>
            <Form.Select
              value={displayKind}
              onChange={(e) => handleDisplayKindChange(e.target.value)}
              disabled={isAccountBase}
            >
              <option value="allocation">Allocation — funding only, no bills</option>
              <option value="bill_estimated">Bill (estimated) — planned bill amount</option>
              <option value="bill_actual">Bill (actual) — tracked recurring bill</option>
            </Form.Select>
          </Form.Group>

          {displayKind === 'allocation' && (
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="is-account-base"
                label="Base allocation (min) for this bank account"
                checked={isAccountBase}
                onChange={(e) => handleAccountBaseChange(e.target.checked)}
              />
              <Form.Text className="text-muted d-block">
                {BASE_ALLOCATION_HELP_TEXT} Amount is in the units of the frequency below (use
                fortnightly for a per-fortnight target).
              </Form.Text>
            </Form.Group>
          )}

          {displayKind === 'allocation' && !isAccountBase && (
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="additive-to-total"
                label="Add on top of minimum / other requirements"
                checked={additiveToTotal}
                onChange={(e) => setAdditiveToTotal(e.target.checked)}
              />
              <Form.Text className="text-muted d-block">
                Counts in addition to the core total (e.g. a dated holiday extra).
              </Form.Text>
            </Form.Group>
          )}

          {(displayKind === 'bill_estimated' || displayKind === 'bill_actual') && (
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="exclude-from-required"
                label="Do not add to bank account required amount"
                checked={excludeFromRequiredAmount}
                onChange={(e) => setExcludeFromRequiredAmount(e.target.checked)}
              />
              <Form.Text className="text-muted d-block">
                Bill is tracked on the Bills page but paid from an allocation envelope.
              </Form.Text>
            </Form.Group>
          )}

          <Form.Group className="mb-3" controlId="requirementAmount">
            <Form.Label>Amount:</Form.Label>
            <InputGroup onClick={stopPropagation}>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                name="amount"
                value={amount}
                onChange={handleInputChange}
                placeholder="Enter amount"
                step="0.01"
                onClick={stopPropagation}
                onWheel={(e) => e.target.blur()}
              />
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3" controlId="requirementFrequency">
            <Form.Label>Frequency:</Form.Label>
            <Form.Select
              name="frequency"
              value={frequency}
              onChange={handleInputChange}
              onClick={stopPropagation}
            >
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </Form.Select>
          </Form.Group>

          {displayKind !== 'allocation' && (
            <>
              <Form.Group className="mb-3" controlId="requirementNextDueDate" onClick={stopPropagation}>
                <Form.Label>Next Due Date:</Form.Label>
                <br />
                <DatePicker
                  selected={nextDueDate}
                  onChange={(date) => setNextDueDate(date)}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  onClick={stopPropagation}
                />
              </Form.Group>

              <Form.Group className="mb-3" onClick={stopPropagation}>
                <Form.Check
                  type="checkbox"
                  id="auto-generate"
                  label="Automatically generate bills"
                  checked={autoGenerate}
                  onChange={(e) => setAutoGenerate(e.target.checked)}
                  onClick={stopPropagation}
                />
              </Form.Group>

              {autoGenerate && (
                <Form.Group className="mb-3">
                  <Form.Label>Minimum unpaid bills to maintain</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="12"
                    value={minUnpaidBills}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10) || 1;
                      setMinUnpaidBills(value);
                    }}
                  />
                  <Form.Text className="text-muted">
                    System will automatically create new bills to maintain this minimum.
                  </Form.Text>
                </Form.Group>
              )}

              {!autoGenerate && (
                <Form.Group className="mb-3" onClick={stopPropagation}>
                  <Form.Check
                    type="checkbox"
                    id="generate-now"
                    label="Generate bills now?"
                    checked={generateNow}
                    onChange={(e) => setGenerateNow(e.target.checked)}
                    onClick={stopPropagation}
                  />
                </Form.Group>
              )}

              {(autoGenerate || generateNow) && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    {autoGenerate ? 'Initial Bills to Create' : 'Number of Bills to Generate'}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="12"
                    value={initialBills}
                    onChange={(e) => setInitialBills(parseInt(e.target.value, 10) || 0)}
                  />
                </Form.Group>
              )}
            </>
          )}

          <Form.Group className="mb-3" controlId="requirementCategory">
            <Form.Label>Category:</Form.Label>
            <Form.Select
              name="category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">-- Select Category --</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="requirementBankAccount" onClick={stopPropagation}>
            <Form.Label>Bank Account:</Form.Label>
            <Form.Select
              name="bank_account"
              value={bankAccountId}
              onChange={(e) => setBankAccountId(e.target.value)}
              onClick={stopPropagation}
            >
              <option value="">-- Select Bank Account --</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.nickname || 'Unnamed'} - {account.bank || 'Unknown Bank'} ({account.acc_num || 'No Account Number'})
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="requirementNotes">
            <Form.Label>Notes:</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={notes}
              onChange={handleInputChange}
              placeholder="Optional notes"
              maxLength={100}
              style={{ resize: 'vertical' }}
            />
          </Form.Group>
        </>
      )}

      {confirmBillDeletion && impactedBills.length > 0 && (
        <div className="mb-3">
          <Alert variant="warning">
            <div className="mb-2"><strong>Warning:</strong> The following unpaid bills will be deleted if you proceed:</div>
            <ul className="mb-2">
              {impactedBills.map(b => (
                <li key={b.id}>{b.due_date} - ${b.amount.toFixed ? b.amount.toFixed(2) : Number(b.amount).toFixed(2)} - {b.name}</li>
              ))}
            </ul>
            <Form.Check
              type="checkbox"
              id="confirm-delete-bills"
              label="I understand and want to delete these bills"
              checked={confirmBillDeletion}
              onChange={(e) => setConfirmBillDeletion(e.target.checked)}
            />
          </Alert>
        </div>
      )}

      {requirement && (
        <div className="requirement-form-secondary mt-4 pt-3 border-top">
          <div className="d-flex flex-wrap gap-2 mb-0">
            <Button
              type="button"
              variant={showConvertPanel ? 'secondary' : 'outline-secondary'}
              size="sm"
              aria-expanded={showConvertPanel}
              onClick={() => setShowConvertPanel((open) => !open)}
            >
              Convert requirement type
              {conversionChanged && (
                <span className="requirement-form-secondary-badge ms-1" aria-label="Conversion pending">
                  •
                </span>
              )}
            </Button>
            {displayKind !== 'allocation' && (
              <Button
                type="button"
                variant={showUpcomingBillsPanel ? 'secondary' : 'outline-secondary'}
                size="sm"
                aria-expanded={showUpcomingBillsPanel}
                onClick={() => setShowUpcomingBillsPanel((open) => !open)}
              >
                Upcoming bills
              </Button>
            )}
          </div>

          {showConvertPanel && (
            <div className="requirement-form-secondary-panel mt-3 p-3 rounded border">
              <h6 className="mb-3">Convert requirement type</h6>
              <Form.Group className="mb-3">
                <Form.Label>Target type</Form.Label>
                <Form.Select
                  value={conversionTargetKind}
                  onChange={(e) => setConversionTargetKind(e.target.value)}
                  disabled={isAccountBase}
                >
                  <option value="allocation">Allocation — funding only, no bills</option>
                  <option value="bill_estimated">Bill (estimated) — planned bill amount</option>
                  <option value="bill_actual">Bill (actual) — tracked recurring bill</option>
                </Form.Select>
                {isAccountBase && (
                  <Form.Text className="text-muted">
                    Base allocation requirements cannot be converted to bill types.
                  </Form.Text>
                )}
              </Form.Group>

              {conversionChanged && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Bill cadence (frequency)</Form.Label>
                    <Form.Select
                      value={conversionFrequency}
                      onChange={(e) => setConversionFrequency(e.target.value)}
                    >
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </Form.Select>
                  </Form.Group>

                  {convertingToBill && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>First due date</Form.Label>
                        <DatePicker
                          selected={conversionNextDueDate}
                          onChange={(date) => setConversionNextDueDate(date)}
                          dateFormat="yyyy-MM-dd"
                          className={`form-control${validationErrors.conversionNextDueDate ? ' is-invalid' : ''}`}
                        />
                        {validationErrors.conversionNextDueDate && (
                          <div className="invalid-feedback d-block">{validationErrors.conversionNextDueDate}</div>
                        )}
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id="conversion-auto-generate"
                          label="Auto-generate bills"
                          checked={conversionAutoGenerate}
                          onChange={(e) => setConversionAutoGenerate(e.target.checked)}
                        />
                      </Form.Group>
                      {conversionAutoGenerate && (
                        <Form.Group className="mb-3">
                          <Form.Label>Min unpaid bills</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            max="12"
                            value={conversionMinUnpaidBills}
                            onChange={(e) => setConversionMinUnpaidBills(parseInt(e.target.value, 10) || 1)}
                          />
                        </Form.Group>
                      )}
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id="conversion-create-initial"
                          label="Create initial bills now"
                          checked={conversionCreateInitialBills}
                          onChange={(e) => setConversionCreateInitialBills(e.target.checked)}
                        />
                      </Form.Group>
                      {conversionCreateInitialBills && (
                        <Form.Group className="mb-3">
                          <Form.Label>Initial bills to create (1-6)</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            max="6"
                            value={conversionInitialBillsCount}
                            onChange={(e) => setConversionInitialBillsCount(parseInt(e.target.value, 10) || 1)}
                          />
                        </Form.Group>
                      )}
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id="conversion-keep-series"
                          label="Keep current bill series (recommended)"
                          checked={conversionKeepSeries}
                          onChange={(e) => setConversionKeepSeries(e.target.checked)}
                        />
                      </Form.Group>
                    </>
                  )}

                  {convertingToAllocation && (
                    <>
                      <Alert variant="danger" className="requirement-conversion-confirm-alert mb-3">
                        <div className="fw-bold mb-2">Type the requirement name to confirm</div>
                        <p className="mb-2">
                          Converting to allocation deletes future unpaid bills in this series.
                        </p>
                        <p className="mb-0">
                          Enter{' '}
                          <strong className="requirement-conversion-confirm-name">{name || 'the requirement name'}</strong>{' '}
                          exactly as shown above.
                        </p>
                      </Alert>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Confirmation</Form.Label>
                        <Form.Control
                          type="text"
                          value={conversionConfirmName}
                          onChange={(e) => setConversionConfirmName(e.target.value)}
                          placeholder="Type the requirement name exactly"
                          className={validationErrors.conversionConfirmName ? 'is-invalid' : ''}
                          autoComplete="off"
                        />
                        {validationErrors.conversionConfirmName && (
                          <div className="invalid-feedback d-block">{validationErrors.conversionConfirmName}</div>
                        )}
                      </Form.Group>
                    </>
                  )}

                  <div
                    className={`requirement-conversion-preview-status small mb-2${
                      conversionError
                        ? ' text-danger'
                        : conversionPreviewLoading
                          ? ' text-muted'
                          : conversionPreview
                            ? ' text-success'
                            : ' text-muted'
                    }`}
                    role="status"
                    aria-live="polite"
                  >
                    {conversionPreviewSummary}
                    {conversionError && needsApiConversionPreview && (
                      <Button
                        type="button"
                        size="sm"
                        variant="link"
                        className="p-0 ms-2 align-baseline requirement-conversion-retry"
                        onClick={fetchConversionPreview}
                      >
                        Retry
                      </Button>
                    )}
                  </div>
                  {validationErrors.conversion && (
                    <div className="text-danger small mb-2">{validationErrors.conversion}</div>
                  )}

                  {conversionPreview?.warnings?.length > 0 && !conversionPreview.local_only && (
                    <Alert variant="info" className="py-2">
                      <ul className="mb-0">
                        {conversionPreview.warnings.map((w) => (
                          <li key={w}>{w}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                  {conversionPreview?.delete_summary?.future_unpaid_count > 0 && (
                    <Alert variant="warning" className="py-2">
                      Will delete {conversionPreview.delete_summary.future_unpaid_count} unpaid future bill(s)
                      {conversionPreview.delete_summary.first_due_date
                        ? ` from ${conversionPreview.delete_summary.first_due_date}`
                        : ''}
                      {conversionPreview.delete_summary.last_due_date
                        ? ` to ${conversionPreview.delete_summary.last_due_date}`
                        : ''}.
                    </Alert>
                  )}
                </>
              )}
            </div>
          )}

          {showUpcomingBillsPanel && displayKind !== 'allocation' && (
            <div className="requirement-form-secondary-panel mt-3 p-3 rounded border">
              <h6 className="mb-3">Upcoming bills</h6>

              <Form.Group className="mb-3" controlId="requirementNextDueDate" onClick={stopPropagation}>
                <Form.Label>Next due date</Form.Label>
                <br />
                <DatePicker
                  selected={nextDueDate}
                  onChange={(date) => setNextDueDate(date)}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  onClick={stopPropagation}
                />
              </Form.Group>

              <Form.Group className="mb-3" onClick={stopPropagation}>
                <Form.Check
                  type="checkbox"
                  id="auto-generate"
                  label="Automatically generate bills"
                  checked={autoGenerate}
                  onChange={(e) => setAutoGenerate(e.target.checked)}
                  onClick={stopPropagation}
                />
              </Form.Group>

              {autoGenerate && (
                <Form.Group className="mb-0">
                  <Form.Label>Minimum unpaid bills to maintain</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="12"
                    value={minUnpaidBills}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10) || 1;
                      setMinUnpaidBills(value);
                    }}
                  />
                </Form.Group>
              )}
            </div>
          )}
        </div>
      )}

      <div className="d-flex justify-content-end mt-4">
        <Button variant="secondary" onClick={onCancel} className="me-2" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={isSubmitting || selectedSegmentIsPast}>
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              {requirement ? 'Updating…' : 'Adding…'}
            </>
          ) : (
            requirement ? 'Update Requirement' : 'Add Requirement'
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RequirementForm;
